import * as React from "react"
import { cn } from "@/lib/utils"
import BumpChart from '@/components/bump-chart'
import axiosInstance from "@/api/axios"
import { useMediaQuery } from "react-responsive"
import { CircleHelp, Download, ChevronDown } from "lucide-react"
import { Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useTranslation } from "react-i18next"

interface Datum {
    x: string;
    y: number;
}

interface ChartData {
    id: string;
    data: Datum[];
}

interface ChartAreaInteractiveProps extends React.HTMLAttributes<HTMLDivElement> {
    className?: string;
    title?: string;
    timeRange?: string;
    bins?: string[];
}

export function BinUsageRanking({ className, title, timeRange, bins = [], ...props }: ChartAreaInteractiveProps) {
    const { t } = useTranslation();
    const [data, setData] = React.useState<ChartData[]>([])
    const [loading, setLoading] = React.useState<boolean>(false)
    const [error, setError] = React.useState<string | null>(null)
    const exportRef = React.useRef<HTMLDivElement>(null)
    const chartContainerRef = React.useRef<HTMLDivElement>(null)

    const getExportData = React.useCallback(() => {
        if (!data || data.length === 0) return null;

        const allDates = Array.from(new Set(data.flatMap(item => item.data.map(d => d.x)))).sort();

        const headers = [t('binUsageRanking.export.month'), ...data.map(item => `${t('binUsageRanking.export.bin')} ${item.id}`)];
        const rows = allDates.map(date => {
            const rowData = [date];
            data.forEach(item => {
                const matchingData = item.data.find(d => d.x === date);
                rowData.push(matchingData ? matchingData.y.toString() : "0");
            });
            return rowData;
        });

        const chartData = data.map(item => ({
            id: item.id,
            data: item.data.map(d => ({
                x: d.x,
                y: d.y
            }))
        }));

        return {
            title: title || t('binUsageRanking.defaultTitle'),
            headers,
            rows,
            chartData
        };
    }, [data, title, t]);

    const handleExportCSV = () => {
        const exportData = getExportData();
        if (!exportData) return;

        const csvContent = [
            exportData.headers.join(","),
            ...exportData.rows.map(row => row.join(","))
        ].join("\n");

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", `bin_usage_ranking_${new Date().toISOString().slice(0, 10)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

const handleExportPNG = async () => {
    if (!exportRef.current) return;

    // Store original classes and styles
    const body = document.body;
    const originalBodyClass = body.className;
    const exportContainer = exportRef.current;
    const originalContainerClass = exportContainer.className;

    // Store original display values for controls
    const exportControls = exportRef.current.querySelector('.bin-usage-export-controls') as HTMLElement;
    const helpCircle = exportRef.current.querySelector('.help-circle') as HTMLElement;
    const originalDisplay = [
        exportControls?.style.display,
        helpCircle?.style.display
    ];

    try {
        // Force dark mode for export
        body.className = 'dark';
        exportContainer.className = cn(originalContainerClass, 'dark');

        // Explicitly set dark background
        const originalBg = exportContainer.style.backgroundColor;
        exportContainer.style.backgroundColor = '#020817'; // Or your dark background color

        // Hide elements temporarily
        if (exportControls) exportControls.style.display = 'none';
        if (helpCircle) helpCircle.style.display = 'none';

        // Add small delay to ensure styles are applied
        await new Promise(resolve => setTimeout(resolve, 100));

        const html2canvas = (await import('html2canvas')).default;
        const canvas = await html2canvas(exportRef.current, {
            scale: 2,
            logging: false,
            useCORS: true,
            backgroundColor: "#020817", // Match your dark background
            windowWidth: exportRef.current.scrollWidth,
            windowHeight: exportRef.current.scrollHeight,
            ignoreElements: (element) => {
                return element.classList.contains('bin-usage-export-controls') ||
                       element.classList.contains('help-circle');
            }
        });

        const link = document.createElement('a');
        link.download = `bin_usage_ranking_${new Date().toISOString().slice(0, 10)}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
    } catch (err) {
        console.error('Failed to export PNG:', err);
    } finally {
        // Restore original classes and styles
        body.className = originalBodyClass;
        exportContainer.className = originalContainerClass;
        exportRef.current.style.backgroundColor = '';

        // Restore original display values
        if (exportControls) exportControls.style.display = originalDisplay[0];
        if (helpCircle) helpCircle.style.display = originalDisplay[1];
    }
};
    React.useEffect(() => {
        setData([])
        setError(null)

        if (!timeRange || !bins || bins.length === 0) {
            console.log("Missing required parameters, skipping data fetch");
            return;
        }

        setLoading(true)

        axiosInstance
            .get("/bin_usage", {
                params: {
                    timeRange: timeRange,
                    bins: bins.join(",")
                },
                paramsSerializer: {
                    indexes: null,
                }
            })
            .then((response) => {
                let chartData: ChartData[];

                if (Array.isArray(response.data)) {
                    chartData = response.data;
                }
                else {
                    setError(t('binUsageRanking.errors.invalidDataFormat'));
                    chartData = [];
                }

                setData(chartData);
            })
            .catch((error) => {
                console.error("Error fetching bin usage data:", error);
                setError(error.message || t('binUsageRanking.errors.failedToLoadData'));
                setData([]);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [timeRange, bins, t]);

    const exportData = getExportData();

    return (
        <div
            ref={exportRef}
            className={cn("lg:col-span-2 aspect-[1/1] sm:aspect-[16/10] sm:rounded-lg bg-muted/50 p-4 exportable-section", className)}
            data-export-data={JSON.stringify(exportData)}
            {...props}
        >
            <div className="relative">
                <div className="flex justify-between items-center pb-1">
                    <div>
                        <h4 className="scroll-m-20 text-xl font-semibold tracking-tight">
                            {title || t('binUsageRanking.defaultTitle')}
                        </h4>
                    </div>
                    <div className="flex items-center space-x-1 bin-usage-export-controls">
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-muted-foreground hover:text-primary"
                                    disabled={!exportData || loading}
                                >
                                    <Download className="h-4 w-4 mr-1" />
                                    {t('binUsageRanking.export.export')}
                                    <ChevronDown className="h-4 w-4 ml-1" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                    onClick={handleExportCSV}
                                    disabled={!exportData || loading}
                                >
                                    {t('binUsageRanking.export.downloadCSV')}
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                    onClick={handleExportPNG}
                                    disabled={!exportData || loading}
                                >
                                    {t('binUsageRanking.export.downloadPNG')}
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                        <TooltipProvider>
                            <UITooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="text-muted-foreground hover:text-primary h-8 w-8 p-0 help-circle"
                                    >
                                        <CircleHelp className="h-4 w-4" />
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent side="left">
                                    <p>{t('binUsageRanking.help.description')}</p>
                                </TooltipContent>
                            </UITooltip>
                        </TooltipProvider>
                    </div>
                </div>
            </div>

            <div
                ref={chartContainerRef}
                className="px-1 pt-2 sm:px-2 sm:pt-2"
                style={{width: '100%', height: '100%'}}
            >
                {loading ? (
                    <div className="flex items-center justify-center h-full w-full">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                    </div>
                ) : error ? (
                    <div className="flex flex-col items-center justify-center h-[400px]">
                        <div className="text-muted-foreground text-center">
                            <p className="mb-2 text-lg">{t('binUsageRanking.states.errorLoadingData')}</p>
                            <p className="text-sm">{error}</p>
                        </div>
                    </div>
                ) : !exportData ? (
                    <div className="flex flex-col items-center justify-center h-[400px]">
                        <div className="text-muted-foreground text-center">
                            <p className="mb-2 text-lg">{t('binUsageRanking.states.noDataAvailable')}</p>
                            <p className="text-sm">{t('binUsageRanking.states.noRankingData')}</p>
                        </div>
                    </div>
                ) : (
                    <div className="w-full h-full">
                        <BumpChart
                            data={exportData.chartData}
                            
                        />
                    </div>
                )}
            </div>
        </div>
    )
}